import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, X, Pencil, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { rebateAlertsAPI } from '../../lib/api';

/**
 * Manager decision controls for ONE pending Special (Phase E). Read-only data
 * comes from the parent; this only issues Authorize / Authorize & Change /
 * Decline against the existing backend and calls onDone() to refresh.
 */
export default function SpecialActionBar({ instance, onDone, authorized = false }) {
    const { t } = useTranslation();
    const orig = instance.original_incentive || {};
    const isCash = orig.kind === 'cash';
    const [mode, setMode] = useState('idle');   // idle | change | decline | busy
    const [amount, setAmount] = useState(orig.amount ?? '');
    const [en, setEn] = useState(orig.label?.en || '');
    const [fr, setFr] = useState(orig.label?.fr || '');
    const [es, setEs] = useState(orig.label?.es || '');
    const [reason, setReason] = useState('');

    const run = async (fn, okKey) => {
        setMode('busy');
        try { await fn(); toast.success(t(okKey)); await onDone?.(); }
        catch { toast.error(t('rebateAlert.toastError')); setMode('idle'); }
    };
    const doAuthorize = () => run(() => rebateAlertsAPI.authorize(instance.id), 'rebateAlert.toastAuthorized');
    const doDecline = () => run(() => rebateAlertsAPI.decline(instance.id, reason || null), 'rebateAlert.toastDeclined');
    const doRevoke = () => run(() => rebateAlertsAPI.revoke(instance.id, reason || null), 'rebateAlert.toastRevoked');
    const doChange = () => {
        const inc = isCash
            ? { kind: 'cash', amount: Number(amount) }
            : { kind: 'perk', label: { en: en.trim(), fr: fr.trim() || null, es: es.trim() || null } };
        return run(() => rebateAlertsAPI.authorize(instance.id, inc), 'rebateAlert.toastAuthorized');
    };
    const changeValid = isCash ? Number(amount) > 0 : en.trim().length > 0;

    if (mode === 'change') {
        return (
            <div className="mt-2 rounded-lg border border-slate-700 bg-slate-900/60 p-2.5 space-y-2" data-testid="special-change-form">
                <p className="text-[11px] font-semibold text-slate-300">{t('rebateAlert.changeTitle')}</p>
                {isCash ? (
                    <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="special-change-amount"
                        placeholder={t('rebateAlert.newAmount')} className="w-full rounded-md bg-slate-800 border border-slate-700 px-2.5 py-1.5 text-sm text-slate-100" />
                ) : (
                    <div className="space-y-1.5">
                        <input value={en} onChange={(e) => setEn(e.target.value)} data-testid="special-change-en" placeholder={t('rebateAlert.newPerkEn')} className="w-full rounded-md bg-slate-800 border border-slate-700 px-2.5 py-1.5 text-sm text-slate-100" />
                        <input value={fr} onChange={(e) => setFr(e.target.value)} data-testid="special-change-fr" placeholder={t('rebateAlert.newPerkFr')} className="w-full rounded-md bg-slate-800 border border-slate-700 px-2.5 py-1.5 text-sm text-slate-100" />
                        <input value={es} onChange={(e) => setEs(e.target.value)} data-testid="special-change-es" placeholder={t('rebateAlert.newPerkEs')} className="w-full rounded-md bg-slate-800 border border-slate-700 px-2.5 py-1.5 text-sm text-slate-100" />
                    </div>
                )}
                <div className="flex gap-2">
                    <Button size="sm" onClick={doChange} disabled={!changeValid} data-testid="special-change-confirm"><Check className="w-3.5 h-3.5 mr-1" />{t('rebateAlert.confirmAuthorize')}</Button>
                    <Button size="sm" variant="ghost" onClick={() => setMode('idle')} data-testid="special-change-cancel">{t('rebateAlert.cancel')}</Button>
                </div>
            </div>
        );
    }
    if (mode === 'decline') {
        return (
            <div className="mt-2 rounded-lg border border-slate-700 bg-slate-900/60 p-2.5 space-y-2" data-testid="special-decline-form">
                <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="special-decline-reason"
                    placeholder={t('rebateAlert.declineReason')} className="w-full rounded-md bg-slate-800 border border-slate-700 px-2.5 py-1.5 text-sm text-slate-100" />
                <div className="flex gap-2">
                    <Button size="sm" variant="destructive" onClick={doDecline} data-testid="special-decline-confirm"><X className="w-3.5 h-3.5 mr-1" />{t('rebateAlert.confirmDecline')}</Button>
                    <Button size="sm" variant="ghost" onClick={() => setMode('idle')} data-testid="special-decline-cancel">{t('rebateAlert.cancel')}</Button>
                </div>
            </div>
        );
    }
    if (mode === 'revoke') {
        return (
            <div className="mt-2 rounded-lg border border-slate-700 bg-slate-900/60 p-2.5 space-y-2" data-testid="special-revoke-form">
                <p className="text-[11px] text-slate-300">{t('rebateAlert.revokeWarn', 'This removes the promotion from the buyer’s screen right away.')}</p>
                <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="special-revoke-reason"
                    placeholder={t('rebateAlert.revokeReason', 'Reason (optional)')} className="w-full rounded-md bg-slate-800 border border-slate-700 px-2.5 py-1.5 text-sm text-slate-100" />
                <div className="flex gap-2">
                    <Button size="sm" variant="destructive" onClick={doRevoke} data-testid="special-revoke-confirm"><Undo2 className="w-3.5 h-3.5 mr-1" />{t('rebateAlert.confirmRevoke', 'Revoke')}</Button>
                    <Button size="sm" variant="ghost" onClick={() => setMode('idle')} data-testid="special-revoke-cancel">{t('rebateAlert.cancel')}</Button>
                </div>
            </div>
        );
    }
    // An AUTHORIZED promotion is live on the buyer's screen, so its only action
    // is taking it back down — authorize/decline apply to pending ones only.
    if (authorized) {
        return (
            <div className="mt-2 flex flex-wrap gap-2" data-testid="special-action-bar-authorized">
                <Button size="sm" variant="outline" onClick={() => setMode('revoke')} disabled={mode === 'busy'} data-testid="special-revoke">
                    <Undo2 className="w-3.5 h-3.5 mr-1" />{t('rebateAlert.revoke', 'Revoke')}
                </Button>
            </div>
        );
    }
    return (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="special-action-bar">
            <Button size="sm" onClick={doAuthorize} disabled={mode === 'busy'} data-testid="special-authorize"><Check className="w-3.5 h-3.5 mr-1" />{t('rebateAlert.authorize')}</Button>
            <Button size="sm" variant="outline" onClick={() => setMode('change')} disabled={mode === 'busy'} data-testid="special-authorize-change"><Pencil className="w-3.5 h-3.5 mr-1" />{t('rebateAlert.authorizeChange')}</Button>
            <Button size="sm" variant="ghost" onClick={() => setMode('decline')} disabled={mode === 'busy'} data-testid="special-decline"><X className="w-3.5 h-3.5 mr-1" />{t('rebateAlert.decline')}</Button>
        </div>
    );
}
